import { useMemo, useState, type Key, type MouseEvent, type ReactNode } from 'react';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';

export type DataTableColumn<TData extends object> = Readonly<{
  key: keyof TData | string;
  header: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  width?: number | string;
  render?: (row: TData) => ReactNode;
  cellSx?: (row: TData) => Record<string, unknown> | undefined;
}>;

export type DataTableAction<TData extends object> = Readonly<{
  label: string;
  color?: 'default' | 'error';
  onClick: (row: TData) => void;
}>;

type DataTableProps<TData extends object> = Readonly<{
  columns: ReadonlyArray<DataTableColumn<TData>>;
  rows: ReadonlyArray<TData>;
  rowKey: keyof TData | ((row: TData, index: number) => Key);
  actions?: ReadonlyArray<DataTableAction<TData>>;
  emptyMessage?: string;
  selectable?: boolean;
  selectedRowKeys?: ReadonlyArray<Key>;
  onSelectionChange?: (selectedRowKeys: Key[]) => void;
  onRowClick?: (row: TData) => void;
  rowSx?: (row: TData) => Record<string, unknown> | undefined;
  footer?: ReactNode;
}>;

type SortDirection = 'asc' | 'desc';

type SortState = {
  key: string;
  direction: SortDirection;
};

function toDisplayText(value: unknown) {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'symbol') {
    return value.description ?? '';
  }

  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  return JSON.stringify(value);
}

function toCellContent(value: ReactNode) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

function toColumnKeyText(key: PropertyKey) {
  return typeof key === 'symbol' ? key.description ?? '' : key.toString();
}

function toRowKeyText(key: Key) {
  return key.toString();
}

function getCellValue<TData extends object>(
  row: TData,
  column: DataTableColumn<TData>
) {
  return row[column.key as keyof TData];
}

function getRowKey<TData extends object>(
  row: TData,
  rowKey: DataTableProps<TData>['rowKey'],
  index: number
) {
  return typeof rowKey === 'function' ? rowKey(row, index) : toDisplayText(row[rowKey]);
}

function compareValues(firstValue: unknown, secondValue: unknown) {
  if (typeof firstValue === 'number' && typeof secondValue === 'number') {
    return firstValue - secondValue;
  }

  return toDisplayText(firstValue).localeCompare(toDisplayText(secondValue), 'ko');
}

function DataTable<TData extends object>({
  columns,
  rows,
  rowKey,
  actions = [],
  emptyMessage = '데이터가 없습니다.',
  selectable = false,
  selectedRowKeys,
  onSelectionChange,
  onRowClick,
  rowSx,
  footer,
}: DataTableProps<TData>) {
  const [sortState, setSortState] = useState<SortState | null>(null);
  const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<Key[]>([]);
  const [actionAnchorEl, setActionAnchorEl] = useState<HTMLElement | null>(null);
  const [actionRow, setActionRow] = useState<TData | null>(null);
  const activeSelectedRowKeys = selectedRowKeys ?? internalSelectedRowKeys;
  const activeSelectedRowKeySet = useMemo(
    () => new Set(activeSelectedRowKeys),
    [activeSelectedRowKeys]
  );
  const rowKeys = useMemo(
    () => rows.map((row, index) => getRowKey(row, rowKey, index)),
    [rowKey, rows]
  );
  const sortedRows = useMemo(() => {
    if (!sortState) {
      return rows;
    }

    const sortableColumn = columns.find((column) => toColumnKeyText(column.key) === sortState.key);

    if (!sortableColumn) {
      return rows;
    }

    return [...rows].sort((firstRow, secondRow) => {
      const result = compareValues(
        getCellValue(firstRow, sortableColumn),
        getCellValue(secondRow, sortableColumn)
      );

      return sortState.direction === 'asc' ? result : -result;
    });
  }, [columns, rows, sortState]);
  const hasActions = actions.length > 0;
  const selectedCount = rowKeys.filter((key) => activeSelectedRowKeySet.has(key)).length;
  const isAllSelected = rowKeys.length > 0 && selectedCount === rowKeys.length;
  const isPartiallySelected = selectedCount > 0 && selectedCount < rowKeys.length;
  const extraColumnCount = (selectable ? 1 : 0) + (hasActions ? 1 : 0);

  const handleSort = (column: DataTableColumn<TData>) => {
    if (!column.sortable) {
      return;
    }

    const nextKey = toColumnKeyText(column.key);

    setSortState((current) => ({
      key: nextKey,
      direction: current?.key === nextKey && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const updateSelectedRowKeys = (nextSelectedRowKeys: Key[]) => {
    setInternalSelectedRowKeys(nextSelectedRowKeys);
    onSelectionChange?.(nextSelectedRowKeys);
  };

  const handleToggleAllRows = () => {
    updateSelectedRowKeys(isAllSelected ? [] : rowKeys);
  };

  const handleToggleRow = (key: Key) => {
    const nextSelectedRowKeys = activeSelectedRowKeySet.has(key)
      ? activeSelectedRowKeys.filter((selectedKey) => selectedKey !== key)
      : [...activeSelectedRowKeys, key];

    updateSelectedRowKeys(nextSelectedRowKeys);
  };

  const handleOpenActionMenu = (event: MouseEvent<HTMLButtonElement>, row: TData) => {
    setActionAnchorEl(event.currentTarget);
    setActionRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ borderRadius: 1, boxShadow: 'none', overflowX: 'auto' }}
    >
      <Table size="small" sx={{ minWidth: 'max-content' }}>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox" sx={{ backgroundColor: '#f8fafc' }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isPartiallySelected}
                  inputProps={{ 'aria-label': '전체 행 선택' }}
                  onChange={handleToggleAllRows}
                  size="small"
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                align={column.align ?? 'left'}
                key={toColumnKeyText(column.key)}
                sx={{
                  width: column.width,
                  backgroundColor: '#f8fafc',
                  color: '#475569',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortState?.key === toColumnKeyText(column.key)}
                    direction={
                      sortState?.key === toColumnKeyText(column.key) ? sortState.direction : 'asc'
                    }
                    onClick={() => handleSort(column)}
                  >
                    {column.header}
                  </TableSortLabel>
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
            {hasActions && (
              <TableCell
                align="center"
                sx={{
                  width: 48,
                  backgroundColor: '#f8fafc',
                  color: '#475569',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                작업
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && !footer ? (
            <TableRow>
              <TableCell
                align="center"
                colSpan={columns.length + extraColumnCount}
                sx={{ color: '#94a3b8', fontSize: 12, height: 96 }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row, rowIndex) => {
              const key = getRowKey(row, rowKey, rowIndex);

              return (
                <TableRow
                  hover
                  key={key}
                  selected={activeSelectedRowKeySet.has(key)}
                  sx={rowSx?.(row)}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={activeSelectedRowKeySet.has(key)}
                        inputProps={{ 'aria-label': `${toRowKeyText(key)} 행 선택` }}
                        onChange={() => handleToggleRow(key)}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      align={column.align ?? 'left'}
                      key={toColumnKeyText(column.key)}
                      onClick={() => onRowClick?.(row)}
                      sx={{
                        color: '#1f2937',
                        cursor: onRowClick ? 'pointer' : undefined,
                        fontSize: 12,
                        ...column.cellSx?.(row),
                      }}
                    >
                      {toCellContent(
                        column.render ? column.render(row) : toDisplayText(getCellValue(row, column))
                      )}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell align="center">
                      <IconButton
                        aria-label={`${toRowKeyText(key)} 행 작업`}
                        onClick={(event) => handleOpenActionMenu(event, row)}
                        size="small"
                      >
                        ⋮
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
          {footer}
        </TableBody>
      </Table>
      {hasActions && (
        <Menu anchorEl={actionAnchorEl} open={Boolean(actionAnchorEl)} onClose={handleCloseActionMenu}>
          {actions.map((action) => (
            <MenuItem
              key={action.label}
              onClick={() => {
                if (actionRow) {
                  action.onClick(actionRow);
                }

                handleCloseActionMenu();
              }}
              sx={{ color: action.color === 'error' ? '#dc2626' : undefined, fontSize: 14 }}
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </TableContainer>
  );
}

export default DataTable;
